import React, { useEffect, useRef, useState } from "react";
import _ from "lodash";
import { Close, Search } from "@mui/icons-material";
import { Box, IconButton, Typography } from "@mui/material";
import { motion } from "framer-motion";
import { colors } from "../../theme/colors";
import { alpha } from "@mui/material/styles";
import FriendRow from "./FriendRow";
import RequestRow from "./RequestRow";
import StatusDot from "../common/StatusDot";
import { useScrollLock } from "../common/lenisContext";
import { toast } from "../../universal/toast";
import { inviteOutcomeEvents } from "./inviteOutcomeToasts";

function SidebarSectionHeader({ label, dot = "online", sx: sxProp }) {
    const dotColumnSx = {
        width: "14px",
        minWidth: "14px",
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        lineHeight: 0,
    };

    const dotSharedSx = {
        fontSize: "0.52rem",
        flexShrink: 0,
        lineHeight: 0,
        display: "flex",
        margin: 0,
        padding: 0,
    };

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                gap: "0.45rem",
                width: "100%",
                mb: "0.5rem",
                mt: 0,
                ...sxProp,
            }}
        >
            {dot !== "none" ? (
                <Box sx={dotColumnSx} aria-hidden>
                    <StatusDot
                        online={dot === "online"}
                        variant="inline"
                        animateGlow={false}
                        sx={dotSharedSx}
                    />
                </Box>
            ) : null}
            <Typography
                sx={{
                    fontSize: "0.58rem",
                    fontWeight: 700,
                    letterSpacing: "0.14em",
                    color: "rgba(255,255,255,0.45)",
                    textTransform: "uppercase",
                    whiteSpace: "nowrap",
                }}
            >
                {label}
            </Typography>
            <Box
                sx={{
                    flex: 1,
                    height: "1px",
                    minWidth: "0.5rem",
                    background:
                        "linear-gradient(90deg, rgba(255,255,255,0.08), rgba(255,255,255,0.02), transparent)",
                }}
            />
        </Box>
    );
}

export default function FriendlistSidebar({
    setSidebarOpen,
    friendRequests,
    handleAcceptFriendRequest,
    handleDeclineFriendRequest,
    handleFriendRequestToCancel,
    friends,
    handleDeleteFriend,
    handleSendInvite,
    inviteState,
    handleClearInviteError,
    actions,
    catalog,
    isLobbyFull = false,
}) {
    const [friendSearchQuery, setFriendSearchQuery] = useState("");
    const [searchFocused, setSearchFocused] = useState(false);
    const prevInviteStateRef = useRef(inviteState);

    useEffect(() => {
        const nicknameByUserId = {};
        for (const friend of friends || []) {
            const userId = friend.friendData?.userId;
            if (userId) {
                nicknameByUserId[userId] = friend.friendData.nickname || "Player";
            }
        }

        const events = inviteOutcomeEvents(
            prevInviteStateRef.current,
            inviteState,
            nicknameByUserId,
            catalog?.user?.userId
        );
        prevInviteStateRef.current = inviteState;

        for (const event of events) {
            toast[event.type](event.message);
            if (event.clearError) {
                handleClearInviteError(event.guestId);
            }
        }
    }, [catalog?.user?.userId, friends, handleClearInviteError, inviteState]);

    useScrollLock(Boolean(catalog?.friendSidebarOpen));

    const filteredFriends = friends.filter((friend) => {
        if (!friendSearchQuery.trim()) return true;
        const query = friendSearchQuery.toLowerCase();
        const friendNickname = friend.friendData?.nickname || friend.friendData?.name || "";
        return friendNickname.toLowerCase().includes(query);
    });

    const onlineFriends = filteredFriends.filter((friend) => friend.friendData?.isOnline);
    const onlineFriendsInYourTeamSorted = _.orderBy(
        onlineFriends,
        [(friend) => !!inviteState[friend.friendData?.userId]],
        ["desc"],
    );
    const offlineFriends = filteredFriends.filter((friend) => !friend.friendData?.isOnline);

    const isLeader = catalog?.user ? catalog.currentLobby?.hostId === catalog.user.userId : false;

    const sendInviteAndClose = (friend) => {
        handleSendInvite(friend);
        setSidebarOpen(false);
    };

    const totalOnlineCount = friends.filter((f) => f.friendData?.isOnline).length;
    const totalFriendsCount = friends.length;

    return (
        <Box
            sx={{
                width: "100%",
                flex: 1,
                height: "100%",
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                alignSelf: "stretch",
                position: "relative",
                zIndex: 1002,
            }}
        >
            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                    position: "relative",
                    p: "1rem",
                    boxSizing: "border-box",
                    background: colors.background.base,
                    backdropFilter: "blur(18px)",
                    WebkitBackdropFilter: "blur(18px)",
                    borderLeft: "1px solid rgba(255,255,255,0.06)",
                    boxShadow:
                        "-18px 0 40px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.04)",
                    overflow: "hidden",
                }}
            >
                <Box
                    sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: "2px",
                        background:
                            "linear-gradient(90deg, transparent, rgba(255,190,90,0.5), transparent)",
                        pointerEvents: "none",
                        zIndex: 3,
                    }}
                />

                <Box
                    sx={{
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        paddingBottom: "0.95rem",
                        gap: "0.5rem",
                        position: "relative",
                        borderBottom: "1px solid rgba(255,255,255,0.05)",
                        "&::after": {
                            content: '""',
                            position: "absolute",
                            bottom: 0,
                            left: "12%",
                            right: "12%",
                            height: "1px",
                            background:
                                "linear-gradient(90deg, transparent, rgba(255,190,90,0.18), transparent)",
                            pointerEvents: "none",
                        },
                    }}
                >
                    <Box sx={{ minWidth: 0, pt: "2px" }}>
                        <Typography
                            sx={{
                                color: colors.text.primary,
                                fontWeight: 600,
                                fontSize: { xs: "1.15rem", xl: "1.35rem" },
                                textTransform: "capitalize",
                                letterSpacing: "0.05em",
                                lineHeight: 1.15,
                            }}
                        >
                            Friends
                        </Typography>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "0.4rem",
                                mt: "0.25rem",
                            }}
                        >
                            {totalOnlineCount > 0 ? (
                                <StatusDot
                                    online
                                    variant="inline"
                                    animateGlow={false}
                                    sx={{ fontSize: "0.55rem" }}
                                />
                            ) : null}
                            <Typography
                                sx={{
                                    color: colors.text.secondary,
                                    fontSize: "0.6rem",
                                    fontWeight: 500,
                                    letterSpacing: "0.05em",
                                    textTransform: "uppercase",
                                }}
                            >
                                {totalOnlineCount} ONLINE - {totalFriendsCount} TOTAL
                            </Typography>
                        </Box>
                    </Box>

                    <IconButton
                        onClick={() => setSidebarOpen(false)}
                        aria-label="Close friends list"
                        sx={{
                            flexShrink: 0,
                            mt: "-2px",
                            width: "1.9rem",
                            height: "1.9rem",
                            borderRadius: "0.55rem",
                            color: colors.text.secondary,
                            backgroundColor: "rgba(255, 255, 255, 0.04)",
                            "&:hover": {
                                backgroundColor: "rgba(255, 255, 255, 0.08)",
                            },
                        }}
                    >
                        <Close sx={{ fontSize: "0.95rem" }} />
                    </IconButton>
                </Box>

                <Box
                    data-lenis-prevent
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        display: "flex",
                        flexDirection: "column",
                        overflow: "auto",
                        scrollbarWidth: "none",
                        msOverflowStyle: "none",
                        "&::-webkit-scrollbar": {
                            display: "none",
                            width: 0,
                            height: 0,
                        },
                    }}
                >
                    <motion.div
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
                        style={{
                            flex: 1,
                            minHeight: 0,
                            display: "flex",
                            flexDirection: "column",
                        }}
                    >
                        {totalFriendsCount > 0 ? (
                        <Box sx={{ mt: "0.75rem", pb: "1rem" }}>
                            <Box
                                sx={{
                                    width: "100%",
                                    minWidth: 0,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "0.75rem",
                                    background: colors.background.surface,
                                    border: `1px solid ${searchFocused ? alpha(colors.primary.main, 0.55) : colors.border.default}`,
                                    borderRadius: "14px",
                                    padding: "0.9rem 1.1rem",
                                    boxShadow: searchFocused
                                        ? `0 0 0 3px ${alpha(colors.primary.main, 0.1)}`
                                        : "none",
                                    transition:
                                        "border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out",
                                }}
                            >
                                <Search
                                    sx={{
                                        flexShrink: 0,
                                        color: searchFocused
                                            ? "rgba(244,194,105,.86)"
                                            : "rgba(255,255,255,.38)",
                                        fontSize: "1.15rem",
                                        transition: "color 180ms ease",
                                    }}
                                />
                                <Box
                                    component="input"
                                    id="friend-search-input"
                                    type="text"
                                    placeholder="Search friends..."
                                    value={friendSearchQuery}
                                    onChange={(event) => setFriendSearchQuery(event.target.value)}
                                    onFocus={() => setSearchFocused(true)}
                                    onBlur={() => setSearchFocused(false)}
                                    sx={{
                                        flex: 1,
                                        minWidth: 0,
                                        background: "none",
                                        border: "none",
                                        outline: "none",
                                        color: colors.text.primary,
                                        fontFamily: "inherit",
                                        fontSize: "0.95rem",
                                        "&::placeholder": {
                                            color: colors.text.muted,
                                        },
                                    }}
                                />
                            </Box>
                        </Box>
                        ) : null}

                        {friendRequests.length > 0 && (
                            <Box sx={{ mt: "1.35rem", mb: "1rem" }}>
                                <SidebarSectionHeader
                                    label={`Requests · ${friendRequests.length}`}
                                    dot="none"
                                />

                                <Box sx={{ overflow: "hidden", pt: "2px" }}>
                                    {friendRequests.map((request, idx) => (
                                        <RequestRow
                                            key={request._id}
                                            request={request}
                                            listIndex={idx}
                                            handleAcceptFriendRequest={handleAcceptFriendRequest}
                                            handleDeclineFriendRequest={handleDeclineFriendRequest}
                                            friendRequestToCancel={handleFriendRequestToCancel}
                                            avatarCacheBust={catalog?.avatarCacheBust || 0}
                                        />
                                    ))}
                                </Box>
                            </Box>
                        )}

                        {friends.length === 0 && friendRequests.length === 0 ? (
                            <Box
                                sx={{
                                    flex: 1,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    px: "1rem",
                                    py: "2rem",
                                }}
                            >
                                <Box sx={{ textAlign: "center", maxWidth: "16rem" }}>
                                    <Typography
                                        sx={{
                                            color: colors.text.primary,
                                            fontSize: "0.88rem",
                                            fontWeight: 600,
                                            letterSpacing: "0.02em",
                                            lineHeight: 1.3,
                                        }}
                                    >
                                        No friends yet
                                    </Typography>
                                    <Typography
                                        sx={{
                                            mt: "0.4rem",
                                            color: colors.text.muted,
                                            fontSize: "0.76rem",
                                            fontWeight: 500,
                                            lineHeight: 1.5,
                                        }}
                                    >
                                        Add someone from their profile and they’ll show up here.
                                    </Typography>
                                </Box>
                            </Box>
                        ) : null}

                        {friends.length > 0 && filteredFriends.length === 0 && (
                            <Typography
                                variant="body2"
                                sx={{
                                    color: "rgba(255,255,255,0.45)",
                                    textAlign: "center",
                                    marginTop: "0.85rem",
                                    fontSize: "0.8rem",
                                    letterSpacing: "0.02em",
                                }}
                            >
                                No friends found matching &quot;{friendSearchQuery}&quot;
                            </Typography>
                        )}

                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                            {onlineFriends.length > 0 && (
                                <Box sx={{ display: "flex", flexDirection: "column", mb: "1rem" }}>
                                    <SidebarSectionHeader
                                        label={`Online · ${onlineFriends.length}`}
                                        dot="online"
                                    />
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: "0.47rem" }}>
                                        {onlineFriendsInYourTeamSorted.map((friend, index) => {
                                            const isInMyLobby =
                                                catalog.currentLobby?.members?.some(
                                                    (m) => m.userId === friend.friendData?.userId,
                                                ) || false;
                                            return (
                                                <FriendRow
                                                    key={`${friend.friendSteamId || friend.friendData?._id}-${index}`}
                                                    friend={friend}
                                                    handleDeleteFriend={handleDeleteFriend}
                                                    sendInvite={sendInviteAndClose}
                                                    inviteState={
                                                        inviteState[friend.friendData?.userId] || {}
                                                    }
                                                    lobbyState={catalog.lobbyState}
                                                    showUnavailableActionMessage={
                                                        actions.showUnavailableActionMessage
                                                    }
                                                    currentLobbyCode={catalog.currentLobbyCode}
                                                    isInMyLobby={isInMyLobby}
                                                    isLobbyFull={isLobbyFull}
                                                    isLeader={isLeader}
                                                    onProfileNavigate={() => setSidebarOpen(false)}
                                                    avatarCacheBust={catalog?.avatarCacheBust || 0}
                                                />
                                            );
                                        })}
                                    </Box>
                                </Box>
                            )}
                            {offlineFriends.length > 0 && (
                                <Box sx={{ display: "flex", flexDirection: "column", mb: "1rem" }}>
                                    <SidebarSectionHeader
                                        label={`Offline · ${offlineFriends.length}`}
                                        dot="offline"
                                    />
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: "0.47rem" }}>
                                        {offlineFriends.map((friend, index) => (
                                            <FriendRow
                                                key={`${friend.friendSteamId || friend.friendData?._id}-${index}`}
                                                friend={friend}
                                                handleDeleteFriend={handleDeleteFriend}
                                                sendInvite={sendInviteAndClose}
                                                avatarCacheBust={catalog?.avatarCacheBust || 0}
                                                onProfileNavigate={() => setSidebarOpen(false)}
                                            />
                                        ))}
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    </motion.div>
                </Box>
            </Box>
        </Box>
    );
}
